import { Box, Fab, Popover, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { useTBContext } from "../../Contexts/useTBStates";
import { IOSSwitch } from "../../../shared/Components/iosSwitch";
import { TabPanel } from "../TabComponents";
import { DivisionBlock } from "../Flighting/DivisionBlock";
import { FlightBlock } from "../Flighting/FlightBlock";
import { addDivision, addFlight, deleteDivision, deleteFlight } from "../Flighting/flightingMutations";
import { StyledIconButton } from "../../../shared/Components/StyledIconButton";
import VisibilityIcon from '@mui/icons-material/Visibility';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { FlightingPreview } from "../Flighting/FlightingPreview";
import { getRegistrationFlightAssignments } from "../Flighting/getRegistrationFlightAssignments";
import React, { useState, useEffect, useMemo, useRef } from "react";
import RefreshIcon from '@mui/icons-material/Refresh';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { useAuth } from "react-oidc-context";
import useApi from "../../../shared/Settings/appSettings";
import { StyledAutocompleteInput } from "../../../shared/Components/StyledAutocompleteInput";
import { allRegistrationFields } from "../../../shared/config/RegistrationFields";
import type { tbEvent } from "@moment-golf/types";
import type { FlightingShape as TypedFlightingShape } from "../Flighting/flightPlacementTypes";

/** Registration properties that can be shown on preview chips (exclude playerName and checkboxes). */
const chipDisplayFieldOptions = [
    { value: '', label: 'None' },
    ...allRegistrationFields
        .filter((f) => f.name !== 'playerName' && f.type !== 'checkbox')
        .map((f) => ({ value: f.name, label: f.label })),
];


interface FlightingTabProps {
    appDims: any;
    compHeights: any;
}

const dataMap = [
    {label: 'Gender', level: 'division', propsField: 'divisionGender'},
    {label: 'Age', level: 'division', propsField: 'divisionAge'},
    {label: 'Hdcp', level: 'division', propsField: 'divisionHdcp'},
    {label: 'Net', level: 'division', propsField: 'divisionNet'},
    {label: 'Qty', level: 'division', propsField: 'divisionQty'},
    {label: 'Manual', level: 'division', propsField: 'divisionManual'},
    {label: 'Gender', level: 'flight', propsField: 'flightGender'},
    {label: 'Age', level: 'flight', propsField: 'flightAge'},
    {label: 'Hdcp', level: 'flight', propsField: 'flightHdcp'},
    {label: 'Net', level: 'flight', propsField: 'flightNet'},
    {label: 'Qty', level: 'flight', propsField: 'flightQty'},
    {label: 'Manual', level: 'flight', propsField: 'flightManual'},

];

const flightingFieldsMap = [
    {index: 0, key: 'gender', level: 'division', field: 'divisionGender', label: 'Gender'},

    {index: 1, key: 'age', level: 'division', field: 'divisionMinAge', label: 'Min Age'},
    {index: 2, key: 'age', level: 'division', field: 'divisionMaxAge', label: 'Max Age'},

    {index: 3, key: 'hdcp', level: 'division', field: 'divisionMinHdcp', label: 'Min Hdcp'},
    {index: 4, key: 'hdcp', level: 'division', field: 'divisionMaxHdcp', label: 'Max Hdcp'},

    {index: 5, key: 'net', level: 'division', field: 'divisionNet', label: 'Net'},

    {index: 6, key: 'qty', level: 'division', field: 'divisionQty', label: 'Qty'},

    {index: 7, key: 'gender', level: 'flight', field: 'flightGender', label: 'Gender'},

    {index: 8, key: 'age', level: 'flight', field: 'flightMinAge', label: 'Min Age'},
    {index: 9, key: 'age', level: 'flight', field: 'flightMaxAge', label: 'Max Age'},

    {index: 10, key: 'hdcp', level: 'flight', field: 'flightMinHdcp', label: 'Min Hdcp'},
    {index: 11, key: 'hdcp', level: 'flight', field: 'flightMaxHdcp', label: 'Max Hdcp'},

    {index: 12, key: 'net', level: 'flight', field: 'flightNet', label: 'Net'},

    {index: 13, key: 'qty', level: 'flight', field: 'flightQty', label: 'Qty'},

];

const dfOptions = [
    {key: 'gender', label: 'Gender'},
    {key: 'age', label: 'Age'},
    {key: 'hdcp', label: 'Hdcp'},
    {key: 'net', label: 'Net'},
    {key: 'qty', label: 'Qty'},
    {key: 'manual', label: 'Manual'},
];

const assignmentModeOptions = [
    { key: 'auto', label: 'Auto' },
    { key: 'manual', label: 'Manual' },
];


/** Runtime shape of event.flighting (tbEvent types it as any) */
type FlightingShape = { props?: Record<string, unknown>; divisions?: unknown[]; flights?: unknown[] };

export const FlightingTab = ({appDims, compHeights}: FlightingTabProps) => {

    const { viewEvent, setViewEvent, eventData, setEventData, viewEventData, setViewEventData, editMode, eventTabValue, factor, appTheme} = useTBContext();
    const auth = useAuth();
    const api = useApi();

    const flighting = viewEvent?.flighting as FlightingShape | undefined;

    const [transitioning, setTransitioning] = useState(false);
    const [populatedFlights, setPopulatedFlights] = useState<any[]>([]);
    const [triggerPlacement, setTriggerPlacement] = useState(false);
    const [previewPopoverOpen, setPreviewPopoverOpen] = useState(false);
    const [previewFullScreen, setPreviewFullScreen] = useState(false);
    const [previewAnchorPosition, setPreviewAnchorPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
    const [chipDisplayField1, setChipDisplayField1] = useState<string>('');
    const [chipDisplayField2, setChipDisplayField2] = useState<string>('');
    const previewFabRef = useRef<HTMLButtonElement>(null);

    const openPreviewPopover = () => {
        const fab = previewFabRef.current;
        if (fab) {
            const rect = fab.getBoundingClientRect();
            setPreviewAnchorPosition({
                left: rect.left,
                top: typeof window !== 'undefined' ? window.innerHeight / 2 : rect.top + rect.height / 2,
            });
        }
        setPreviewPopoverOpen(true);
    };

    // Derive from props first; if missing, infer from division/flight data so selection displays after load/save
    const divisionControl = useMemo(() => {
        const props = flighting?.props as Record<string, unknown> | undefined;
        if (props) {
            const fromProps = Object.keys(props).find(k =>
                k.startsWith('division') && props[k] && k !== 'divisions'
            );
            if (fromProps) return fromProps.replace('division', '').toLowerCase();
        }
        // Infer from division objects (e.g. saved data has divisionGender on divisions but props not set)
        const divisions = (flighting?.divisions ?? []) as any[];
        const first = divisions[0];
        if (!first || typeof first !== 'object') return null;
        const divisionFieldEntry = flightingFieldsMap.find(
            (e) => e.level === 'division' && first.hasOwnProperty(e.field)
        );
        return divisionFieldEntry ? divisionFieldEntry.key : null;
    }, [flighting?.props, flighting?.divisions]);

    const flightControl = useMemo(() => {
        const props = flighting?.props as Record<string, unknown> | undefined;
        if (props) {
            const fromProps = Object.keys(props)
                .filter(k =>
                    k.startsWith('flight') && props[k] && !['flights', 'flightingFields'].includes(k)
                )
                .map(k => k.replace('flight', '').toLowerCase());
            if (fromProps.length) return fromProps;
        }
        // Infer from flight objects
        const flights = (flighting?.flights ?? []) as any[];
        const first = flights[0];
        if (!first || typeof first !== 'object') return [];
        const keys = new Set<string>();
        flightingFieldsMap
            .filter((e) => e.level === 'flight' && first.hasOwnProperty(e.field))
            .forEach((e) => keys.add(e.key));
        return Array.from(keys);
    }, [flighting?.props, flighting?.flights]);

    // When no flights exist, treat each division as a single flight for placement and display
    const effectiveFlights = useMemo(() => {
        const flights = (flighting?.flights ?? []) as any[];
        const divisions = (flighting?.divisions ?? []) as any[];
        if (flights.length > 0) return flights;
        if (divisions.length > 0) {
            return divisions.map((division: any, i: number) => ({
                divisionIndex: i,
                flightName: division.divisionName ?? `Division ${i + 1}`,
                index: i,
                players: [] as any[],
                divisionOnly: true,
            }));
        }
        return [];
    }, [flighting?.flights, flighting?.divisions]);

    const useDivisionsAsFlights = (flighting?.flights ?? []).length === 0 && ((flighting?.divisions ?? []) as any[]).length > 0;

    useEffect(() => {
        if (effectiveFlights.length === 0) return;
        const divisions = (flighting?.divisions ?? []) as any[];
        const assignmentMode = (flighting?.props as Record<string, unknown>)?.assignmentMode as string | undefined || 'auto';
        const registrations = viewEventData?.registrations ?? [];

        const baseFlights = effectiveFlights.map((flight: any) => ({
            ...flight,
            players: Array.isArray(flight.players) ? [...flight.players] : []
        }));

        if (assignmentMode === 'manual' && registrations.length > 0) {
            const populated = baseFlights.map((f: any) => ({ ...f, players: [] }));
            registrations.forEach((reg: any) => {
                const divName = reg.division ?? reg.divisionName;
                const division = divisions.find((d: any) => (d.divisionName || '').toString() === (divName || '').toString());
                if (division == null) return;
                if (useDivisionsAsFlights) {
                    // Place by division only when no real flights
                    const popFlight = populated.find((p: any) => p.divisionIndex === division.index);
                    if (popFlight) popFlight.players.push(reg);
                } else {
                    const flightName = reg.flight ?? reg.flightName;
                    if (flightName == null) return;
                    const flight = effectiveFlights.find((f: any) => f.divisionIndex === division.index && (f.flightName || '').toString() === (flightName || '').toString());
                    if (flight == null) return;
                    const popFlight = populated.find((p: any) => p.index === flight.index);
                    if (popFlight) popFlight.players.push(reg);
                }
            });
            setPopulatedFlights(populated);
        } else {
            setPopulatedFlights(baseFlights);
        }
    }, [viewEvent, flighting, viewEventData?.registrations, effectiveFlights, useDivisionsAsFlights]);

    // When flightPricing is on, backfill divisionPrice/flightPrice and ensure flightingFields has Price
    useEffect(() => {
        if (!viewEvent?.flightPricing || !viewEvent?.flighting) return;
        const f = viewEvent.flighting as FlightingShape;
        const divisions = (f?.divisions ?? []) as any[];
        const flights = (f?.flights ?? []) as any[];
        const props = (f?.props ?? {}) as Record<string, unknown>;
        const flightingFields = (props.flightingFields ?? []) as any[];
        const hasPriceField = flightingFields.some((x: any) => x.field === 'flightPrice');
        const divisionsNeedPrice = divisions.some((d: any) => d && !('divisionPrice' in d));
        const flightsNeedPrice = flights.some((fl: any) => fl && !('flightPrice' in fl));
        if (!divisionsNeedPrice && !flightsNeedPrice && (flights.length === 0 || hasPriceField)) return;
        setViewEvent((prev) => {
            if (!prev?.flightPricing || !prev.flighting) return prev;
            const prevF = prev.flighting as FlightingShape;
            const prevDivisions = (prevF.divisions ?? []) as any[];
            const prevFlights = (prevF.flights ?? []) as any[];
            const prevProps = (prevF.props ?? {}) as Record<string, unknown>;
            const prevFields = (prevProps.flightingFields ?? []) as any[];
            const nextDivisions = prevDivisions.map((d: any) => (d && !('divisionPrice' in d) ? { ...d, divisionPrice: 0 } : d));
            const nextFlights = prevFlights.map((fl: any) => (fl && !('flightPrice' in fl) ? { ...fl, flightPrice: 0 } : fl));
            let nextFields = prevFields;
            if (prevFlights.length > 0 && !prevFields.some((x: any) => x.field === 'flightPrice')) {
                nextFields = [...prevFields, { key: 'price', level: 'flight', field: 'flightPrice', label: 'Price' }];
            }
            return {
                ...prev,
                flighting: {
                    ...prevF,
                    divisions: nextDivisions,
                    flights: nextFlights,
                    props: { ...prevProps, flightingFields: nextFields },
                },
            } as tbEvent;
        });
    }, [viewEvent?.flightPricing, viewEvent?.flighting, setViewEvent]);

    const handleSave = async () => {
        if (!viewEvent) return;
        const divisions = (flighting?.divisions ?? []) as { divisionName?: string; index: number }[];
        const assignments = getRegistrationFlightAssignments(
            populatedFlights,
            divisions,
            useDivisionsAsFlights
        );
        const updatedRegistrations = (viewEventData?.registrations ?? []).map((reg: any) => {
            const a = assignments.find((x) => x._id === reg._id);
            if (!a) return reg;
            return {
                ...reg,
                division: a.division,
                divisionName: a.divisionName,
                flight: a.flight,
                flightName: a.flightName,
            };
        });
        const changedRegistrations = updatedRegistrations.filter((reg: any) => {
            const orig = (viewEventData?.registrations ?? []).find((r: any) => r._id === reg._id);
            if (!orig) return false;
            return (
                orig.division !== reg.division ||
                orig.divisionName !== reg.divisionName ||
                orig.flight !== reg.flight ||
                orig.flightName !== reg.flightName
            );
        });
        const toSave = changedRegistrations.length > 0
            ? changedRegistrations.map((r: any) => {
                const { hasUserID, hasEventScoreID, ...rest } = r;
                return rest;
              })
            : [];
        if (toSave.length > 0) {
            await api
                .post("/tb/UpdateRegistrations", {
                    user_userID: auth.user?.profile.sub,
                    registrations: toSave,
                })
                .then(() => {
                    setViewEventData({ ...viewEventData, registrations: updatedRegistrations });
                })
                .catch((err: any) => console.error(err));
        }
        // Always persist event flighting (flights layout) even when no registration division/flight changed
        const updatedViewEvent = JSON.parse(JSON.stringify(viewEvent)) as typeof viewEvent;
        const flightingPayload = updatedViewEvent.flighting as FlightingShape;
        // Only persist real flights; when divisions-as-flights, do not save synthetic flights
        flightingPayload.flights = useDivisionsAsFlights ? (flighting?.flights ?? []) : populatedFlights;

        await api.post('/tb/UpdateEvent', {
            _id: viewEvent._id,
            userID: auth.user?.profile.sub,
            flighting: flightingPayload
        })
        .then((res: any) => {
            setViewEvent(JSON.parse(JSON.stringify(updatedViewEvent)));
            setEventData(eventData.map((event: any) =>
                event._id === viewEvent._id ?
                    JSON.parse(JSON.stringify(updatedViewEvent)) : event));
        })
        .catch((err: any) => {
            console.error(err);
        });
    }

    const handleRefreshToEvent = () => {
        if (!viewEvent) return;
        if (useDivisionsAsFlights) {
            setPopulatedFlights(effectiveFlights.map((f: any) => ({ ...f, players: [] })));
            return;
        }
        const ev = eventData.find((event: any) => event._id === viewEvent._id);
        const eventOrgFlights = JSON.parse(JSON.stringify((ev?.flighting as FlightingShape)?.flights ?? []));

        const updatedFlights = (flighting?.flights ?? []).map((flight: any) => {
            const orgFlight = eventOrgFlights.find((f: any) => f.index === flight.index);
            return {
                ...flight,
                players: orgFlight ? [...orgFlight.players] : []
            }
        });

        setPopulatedFlights(updatedFlights);

        setViewEvent((prev) => (prev ? { ...prev, flighting: { ...(prev.flighting as FlightingShape), flights: updatedFlights } } : null) as tbEvent | null);
    }

    const handleDivisionFieldsChange = (newValue: any) => {
        if (!viewEvent || !flighting) return;
        const updatedFlighting = JSON.parse(JSON.stringify(flighting));

        if (newValue == null) {
            console.debug('[FlightingTab] Division Control cleared (null)', { previous: divisionControl });
            Object.keys(updatedFlighting.props).forEach((item: any) => {
                if (item.includes('division') && item !== 'divisions') {
                    updatedFlighting.props[item] = false;
                }
            });
            updatedFlighting.props.flightingFields = (updatedFlighting.props.flightingFields || []).filter((f: any) => f.level !== 'division');
            updatedFlighting.divisions = (updatedFlighting.divisions || []).map((d: any) => ({ index: d.index, divisionName: d.divisionName }));
            setViewEvent((prev) => (prev ? { ...prev, flighting: updatedFlighting } : null) as tbEvent | null);
            return;
        }

        console.debug('[FlightingTab] Division Control onChange', { newValue: newValue?.key ?? newValue?.label, options: dfOptions.map((o: any) => o.key) });
        // New fields to add with switch on
        const matchFields = flightingFieldsMap.filter((item: any) => item.key === newValue.label.toLowerCase() && item.level === 'division') || [];

        // Fields in flights minus same label
        const toggleOnKeepFields = updatedFlighting.props.flightingFields.filter((item: any) => 
            !(item.key === divisionControl && item.level === 'division') && // if current
            !(item.key === newValue.label.toLowerCase() && item.level !== 'division')) || []; // if not same type other level


        const removeFields = updatedFlighting.props.flightingFields.filter((item: any) => 
            (item.level === 'division' && !['index', 'divisionName'].includes(item.key)) ||
            (item.level === 'flight' && item.key === newValue.label.toLowerCase())
        ) || [];

        // updates the flighting fields
        Object.keys(updatedFlighting.props).forEach((item: any) => {
            if (item === `division${newValue.label}`) {
                updatedFlighting.props[`division${newValue.label}`] = true;
            } else if (item.includes('division') && item !== 'divisions') {
                updatedFlighting.props[item] = false;
            } else if (item === `flight${newValue.label}`) {
                updatedFlighting.props[`flight${newValue.label}`] = false;
            }
        });

        updatedFlighting.props.flightingFields = [...toggleOnKeepFields, ...matchFields];

        const defaultValue = newValue.label === 'Net' ? true : (
            newValue.label === 'Age' || newValue.label === 'Hdcp' ? 0 : null
        );


        // Remove old division fields and add new ones
        updatedFlighting.divisions = updatedFlighting.divisions.map((division: any) => {
            const divisionCopy = {...division};
            
            // Remove all existing division fields except index and name
            removeFields.forEach((field: any) => {
                delete divisionCopy[field.field];
            });

            // Add new fields with default values
            return {
                ...divisionCopy,
                ...matchFields.reduce((acc: any, field: any) => ({
                    ...acc,
                    [field.field]: defaultValue
                }), {})
            };
        });

        // Remove flight fields
        updatedFlighting.flights = updatedFlighting.flights.map((flight: any) => {
            const flightCopy = {...flight};
            
            // Remove all existing flight fields except index and name
            removeFields.forEach((field: any) => {
                delete flightCopy[field.field];
            });
            
            return flightCopy;  // Return the modified flight object
        });

        setViewEvent((prev) => (prev ? { ...prev, flighting: updatedFlighting } : null) as tbEvent | null);
    };

    const handleFlightFieldsChange = (newValue: any) => {
        if (!viewEvent || !flighting) return;
        const updatedFlighting = JSON.parse(JSON.stringify(flighting));
        const selected = Array.isArray(newValue) ? newValue : (newValue != null ? [newValue] : []);
        console.debug('[FlightingTab] Flight Control onChange', { newValue: selected.map((v: any) => v?.key ?? v?.label), count: selected.length });

        // New fields to add with switch on - modified to handle array of values
        const matchFields = flightingFieldsMap.filter((item: any) => 
            selected.some((val: any) => val.key === item.key && item.level === 'flight')
        ) || [];

        // Fields in flights minus same label
        const toggleOnKeepFields = updatedFlighting.props.flightingFields.filter((item: any) => 
            !(flightControl.includes(item.key) && item.level === 'flight') && // if current
            !selected.some((val: any) => val.key === item.key && item.level !== 'flight') // if not same type other level
        ) || [];

        const removeFields = updatedFlighting.props.flightingFields.filter((item: any) => 
            (item.level === 'flight' && !selected.some((val: any) => val.key === item.key) && item.field !== 'flightName')
        ) || [];

        updatedFlighting.props.flightingFields = [...toggleOnKeepFields, ...matchFields];

        // updates the flighting fields
        // Update flighting props
        Object.keys(updatedFlighting.props).forEach((item: any) => {
            
            if (matchFields.some((field: any) => 
                    field.key === item.replace('flight', '').toLowerCase() 
                    && field.level === 'flight'
                )) {
                updatedFlighting.props[item] = true;
            }

            if (removeFields.some((field: any) => field.key === item.replace('flight', '').toLowerCase() && field.level === 'flight')) {
                updatedFlighting.props[item] = false;
            }

        });

        const defaultValue  = (newValue: any) => {

           return newValue.label === 'Net' ? true : (
                newValue.label === 'Age' || newValue.label === 'Hdcp' ? 0 : null
            )
        };

        // Remove flight fields
        updatedFlighting.flights = updatedFlighting.flights.map((flight: any) => {
            const flightCopy = {...flight};
            
            // Remove all existing flight fields except index and name
            removeFields.forEach((field: any) => {
                delete flightCopy[field.field];
            });

            // Add new field with default value
            matchFields.forEach((field: any) => {
                if (!flightCopy.hasOwnProperty(field.field)) {
                    flightCopy[field.field] = defaultValue(selected.find((item: any) => item.key === field.key));
                }
            });

            return flightCopy;
        });

        setViewEvent((prev) => (prev ? { ...prev, flighting: updatedFlighting } : null) as tbEvent | null);
    };

    const handleAddDivision = () => {
        if (!viewEvent?.flighting) return;
        const updatedFlighting = addDivision(
            viewEvent.flighting as TypedFlightingShape,
            viewEvent.flightPricing
        );
        setViewEvent((prev) => (prev ? { ...prev, flighting: updatedFlighting } : null) as tbEvent | null);
    };

    const handleDeleteDivision = (divisionIndex: number) => {
        if (!viewEvent?.flighting) return;
        const updatedFlighting = deleteDivision(
            viewEvent.flighting as TypedFlightingShape,
            divisionIndex
        );
        setViewEvent((prev) => (prev ? { ...prev, flighting: updatedFlighting } : null) as tbEvent | null);
    };

    const handleAddFlight = (divisionIndex: number, templateFlight: any) => {
        if (!viewEvent?.flighting) return;
        const updatedFlighting = addFlight(
            viewEvent.flighting as TypedFlightingShape,
            divisionIndex,
            templateFlight,
            viewEvent.flightPricing
        );
        setViewEvent((prev) => (prev ? { ...prev, flighting: updatedFlighting } : null) as tbEvent | null);
    };

    const handleDeleteFlight = (flightIndex: number) => {
        if (!viewEvent?.flighting) return;
        const updatedFlighting = deleteFlight(
            viewEvent.flighting as TypedFlightingShape,
            flightIndex
        );
        setViewEvent((prev) => (prev ? { ...prev, flighting: updatedFlighting } : null) as tbEvent | null);
    };

    const flightsEnabled = !!(flighting?.props as Record<string, unknown>)?.flights;
    const divisions = (flighting?.divisions ?? []) as any[];

    return (
        <TabPanel value={eventTabValue} index={1} appDims={appDims} compHeights={compHeights} factor={factor} > {/* Format Tab */}
            <Stack id={'tab-panel-1-content'} direction='row' spacing={3} height={'100%'} sx={{ position: 'relative' }}>
                <Stack 
                    direction='column' 
                    spacing={3} 
                    onTransitionEnd={() => setTransitioning(false)}
                    sx={{ 
                        flex: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                        transition: 'all 0.3s ease-in-out',
                        opacity: 1,
                    }}
                >
                    <Stack direction='column' spacing={2}>
                        <Stack direction='row' gap={3} flexWrap={'wrap'} height={100*factor} sx={{ overflow: 'hidden' }}>
                            <Stack direction='column' spacing={2} /* flex={1} */ width={'max-content'} alignItems={'center'}>
                                <Stack direction='row' spacing={1} alignItems='center' sx={{ height: 25*factor }}>
                                    <Typography variant='subtitle2' color='primary' sx={{ fontWeight: 500}}>Divisions</Typography>
                                    <IOSSwitch

                                        checked={!!(flighting?.props as Record<string, unknown>)?.divisions}
                                        disabled={!editMode}
                                        onChange={(e: any) => {

                                            if (e.target.checked) {
                                                const withPrice = (prev: tbEvent) => prev?.flightPricing ? [{ index: 0, divisionName: 'Division 1', divisionPrice: 0 }, { index: 1, divisionName: 'Division 2', divisionPrice: 0 }] : [{ index: 0, divisionName: 'Division 1' }, { index: 1, divisionName: 'Division 2' }];
                                                setViewEvent((prev) => (prev ? { ...prev, flighting: { ...(prev.flighting as FlightingShape), props: { ...(prev.flighting as FlightingShape)?.props, divisions: true }, divisions: withPrice(prev) } } : null) as tbEvent | null);
                                            } else {
                                                setViewEvent((prev) => (prev ? { ...prev, flighting: { ...(prev.flighting as FlightingShape), props: { ...(prev.flighting as FlightingShape)?.props, divisions: false }, divisions: [] } } : null) as tbEvent | null);
                                            }
                                        }}
                                    /> 
                                </Stack>

                                <Stack direction='row' spacing={3}
                                    sx={{ 
                                        transition: 'all 0.3s ease-in-out',
                                        opacity: transitioning ? 0.5 : 1,
                                    }}
                                    >
                                    <StyledAutocompleteInput
                                        disabled={!editMode}
                                        appTheme={appTheme}
                                        factor={factor}
                                        options={dfOptions}
                                        value={dfOptions.find((option) => option.key === divisionControl) || null}
                                        getOptionLabel={(option: any) => option?.label || ''}
                                        label='Division Control'
                                        width={150}
                                        onChange={(_event: any, newValue: any) => {
                                            handleDivisionFieldsChange(newValue);
                                        }}
                                        isOptionEqualToValue={(option, value) => option?.key === value?.key}
                                    />
                                </Stack>
                            </Stack>

                            <Stack direction='column' spacing={2} flex={1} maxWidth={450} alignItems={'center'}>
                                <Stack direction='row' spacing={1}>

                                    <Typography variant='subtitle2' color='primary' sx={{ fontWeight: 500 }}>Flights</Typography>
                                    <IOSSwitch
                                        checked={!!(flighting?.props as Record<string, unknown>)?.flights}
                                        disabled={!editMode}
                                        onChange={(e: any) => {

                                            if (e.target.checked) {
                                                const divisions = (flighting?.divisions?.length)
                                                    ? (flighting.divisions as any[])
                                                    : [{ index: 0, divisionName: 'Division 1' }];
                                                const ensurePropsDivisions = !flighting?.divisions?.length;
                                                const baseFlightingFields = [{ key: 'flight', level: 'flight', field: 'flightName', label: 'Flight' }];
                                                const flightingFieldsWithPrice = (prev: tbEvent) => prev?.flightPricing
                                                    ? [...baseFlightingFields, { key: 'price', level: 'flight' as const, field: 'flightPrice', label: 'Price' }]
                                                    : baseFlightingFields;
                                                setViewEvent((prev) => {
                                                    if (!prev) return null;
                                                    const fields = flightingFieldsWithPrice(prev);
                                                    const newFlights = divisions.flatMap((division: any, index: number) => {
                                                        const base = { divisionIndex: index, flightName: 'Flight 1', index };
                                                        return prev.flightPricing ? [{ ...base, flightPrice: 0 }] : [base];
                                                    });
                                                    return { ...prev, flighting: { ...(prev.flighting as FlightingShape), ...(ensurePropsDivisions && { divisions }), props: { ...(prev.flighting as FlightingShape)?.props, flights: true, ...(ensurePropsDivisions && { divisions: true }), flightingFields: fields }, flights: newFlights } } as tbEvent;
                                                });
                                            } else {
                                                const prevFlighting = viewEvent?.flighting as FlightingShape;
                                                const flightingFields = (prevFlighting?.props as any)?.flightingFields ?? [];
                                                setViewEvent((prev) => (prev ? { ...prev, flighting: { ...(prev.flighting as FlightingShape), props: { ...(prev.flighting as FlightingShape)?.props, flights: false, flightingFields: flightingFields.filter((field: any) => field.key !== 'flight') }, flights: [] } } : null) as tbEvent | null);
                                            }
                                            
                                        }}
                                    /> 
                                </Stack>

                                {flightsEnabled && (
                                    <Stack direction='row' spacing={3}
                                    sx={{ 
                                        transition: 'all 0.3s ease-in-out',
                                        opacity: transitioning ? 0.5 : 1,
                                    }}
                                    >
                                        <StyledAutocompleteInput
                                            disabled={!editMode}
                                            appTheme={appTheme}
                                            factor={factor}
                                            options={divisionControl != null ? dfOptions.filter((option) => option.key !== divisionControl) : dfOptions}
                                            value={(divisionControl != null ? dfOptions.filter((o) => o.key !== divisionControl) : dfOptions).filter((option) => flightControl.includes(option.key))}
                                            getOptionLabel={(option) => option.label}
                                            label='Flight Control'
                                            multiple={true}
                                            width={150}
                                            onChange={(_event: any, newValue: any) => {
                                                handleFlightFieldsChange(newValue ?? []);
                                            }}
                                            isOptionEqualToValue={(option, value) => option?.key === value?.key}
                                        />
                                    </Stack>
                                )}
                            </Stack>

                            {(!!(flighting?.props as Record<string, unknown>)?.divisions || !!(flighting?.props as Record<string, unknown>)?.flights) && (
                                <Stack direction='column' spacing={2} width={'max-content'} alignItems={'center'}>
                                    <Typography variant='subtitle2' color='primary' sx={{ fontWeight: 500 }}>Assignment</Typography>
                                    <StyledAutocompleteInput
                                        disabled={!editMode}
                                        appTheme={appTheme}
                                        factor={factor}
                                        options={assignmentModeOptions}
                                        value={assignmentModeOptions.find((o) => {
                                            const saved = (flighting?.props as Record<string, unknown>)?.assignmentMode;
                                            const key = (saved === 'user' ? 'manual' : String(saved ?? 'auto')) as string;
                                            return o.key === key;
                                        }) ?? null}
                                        getOptionLabel={(option: any) => option?.label || ''}
                                        label='Assignment'
                                        width={150}
                                        isOptionEqualToValue={(option, value) => option?.key === value?.key}
                                        onChange={(_e: any, newValue: any) => {
                                            const mode = (newValue?.key ?? 'auto') as 'auto' | 'manual';
                                            console.debug('[FlightingTab] Assignment onChange', { newValue: newValue?.key ?? null, mode });
                                            setViewEvent((prev) => (prev ? { ...prev, flighting: { ...(prev.flighting as FlightingShape), props: { ...(prev.flighting as FlightingShape)?.props, assignmentMode: mode } } } : null) as tbEvent | null);
                                        }}
                                    />
                                </Stack>
                            )}
                        </Stack>
                    </Stack>


                    {!!(flighting?.props as Record<string, unknown>)?.divisions && (
                        <Box flex={1} sx={{ padding: '10px', overflowY: 'auto', overflowX: 'hidden' }}>
                            <Stack direction='column' spacing={2}>
                                {divisions.map((division: any, index: number) => (
                                    <React.Fragment key={division.index ?? index}>
                                        <DivisionBlock
                                            division={division}
                                            index={index}
                                            appTheme={appTheme}
                                            factor={factor}
                                            editMode={editMode}
                                            divisionCount={divisions.length}
                                            onAddDivision={handleAddDivision}
                                            onDeleteDivision={handleDeleteDivision}
                                        />
                                        {effectiveFlights.some((f: any) => f.divisionIndex === index && !f.divisionOnly) && viewEvent ? (
                                            <FlightBlock
                                                divisionIndex={index}
                                                viewEvent={viewEvent}
                                                editMode={editMode}
                                                appTheme={appTheme}
                                                factor={factor}
                                                transitioning={transitioning}
                                                flightsToShow={effectiveFlights}
                                                isDivisionsOnly={useDivisionsAsFlights}
                                                onAddFlight={handleAddFlight}
                                                onDeleteFlight={handleDeleteFlight}
                                            />
                                        ) : null}
                                    </React.Fragment>
                                ))}
                            </Stack>
                        </Box>
                    )}
                </Stack>

                <Tooltip title="Preview Flighting Fields">
                    <Fab
                        ref={previewFabRef}
                        color="primary"
                        aria-label="Preview Flighting Fields"
                        onClick={openPreviewPopover}
                        sx={{
                            position: 'absolute',
                            top: 24,
                            right: 24,
                        }}
                    >
                        <VisibilityIcon />
                    </Fab>
                </Tooltip>

                <Popover
                    open={previewPopoverOpen}
                    anchorReference="anchorPosition"
                    anchorPosition={previewAnchorPosition}
                    onClose={() => setPreviewPopoverOpen(false)}
                    anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'center', horizontal: 'right' }}
                    PaperProps={{
                        sx: {
                            width: previewFullScreen ? '90vw' : '60vw',
                            height: previewFullScreen ? '90vh' : '60vh',
                            display: 'flex',
                            flexDirection: 'column',
                            borderRadius: 2,
                            p: 2,
                        },
                    }}
                >
                    <Stack direction="column" spacing={2} sx={{ height: '100%', minHeight: 0 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
                            <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 500 }}>
                                Preview Players by Flight
                            </Typography>
                            <StyledIconButton
                                icon={previewFullScreen ? <FullscreenExitIcon sx={{ marginRight: '5px' }} /> : <FullscreenIcon sx={{ marginRight: '5px' }} />}
                                label={previewFullScreen ? 'Exit full screen' : 'Full screen'}
                                appTheme={appTheme}
                                factor={factor}
                                color="primary"
                                width="max-content"
                                height={30 * factor}
                                onClick={() => setPreviewFullScreen((prev) => !prev)}
                            />
                        </Stack>
                        
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <StyledIconButton
                                icon={<RestartAltOutlinedIcon sx={{ marginRight: '5px' }} />}
                                label="Reset Players"
                                appTheme={appTheme}
                                factor={factor}
                                color="primary"
                                width="max-content"
                                height={30 * factor}
                                onClick={() => handleRefreshToEvent()}
                            />
                            <StyledIconButton
                                icon={<PlayCircleOutlineIcon sx={{ marginRight: '5px' }} />}
                                label="Preview"
                                appTheme={appTheme}
                                factor={factor}
                                color="primary"
                                width="max-content"
                                height={30 * factor}
                                onClick={() => setTriggerPlacement(!triggerPlacement)}
                            />
                            <StyledIconButton
                                icon={<SaveOutlinedIcon sx={{ marginRight: '5px' }} />}
                                label="Save"
                                appTheme={appTheme}
                                factor={factor}
                                color="primary"
                                width="max-content"
                                height={30 * factor}
                                onClick={() => handleSave()}
                            />
                            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography variant="body2" color="primary.main" sx={{ fontWeight: 500 }}>
                                Chip display:
                            </Typography>
                            <StyledAutocompleteInput
                                appTheme={appTheme}
                                factor={factor}
                                options={chipDisplayFieldOptions}
                                value={chipDisplayFieldOptions.find((o) => o.value === chipDisplayField1) ?? chipDisplayFieldOptions[0]}
                                getOptionLabel={(o: { value: string; label: string }) => o?.label ?? ''}
                                label="Field 1"
                                width={140}
                                onChange={(_e: any, v: { value: string; label: string } | null) => {
                                    const next = v?.value ?? '';
                                    setChipDisplayField1(next);
                                    if (next && next === chipDisplayField2) setChipDisplayField2('');
                                }}
                                isOptionEqualToValue={(o, v) => o.value === v?.value}
                            />
                            <StyledAutocompleteInput
                                appTheme={appTheme}
                                factor={factor}
                                options={chipDisplayFieldOptions.filter((o) => o.value !== chipDisplayField1)}
                                value={chipDisplayField2 && chipDisplayField2 !== chipDisplayField1 ? (chipDisplayFieldOptions.find((o) => o.value === chipDisplayField2) ?? chipDisplayFieldOptions[0]) : chipDisplayFieldOptions[0]}
                                getOptionLabel={(o: { value: string; label: string }) => o?.label ?? ''}
                                label="Field 2"
                                width={140}
                                onChange={(_e: any, v: { value: string; label: string } | null) => setChipDisplayField2(v?.value ?? '')}
                                isOptionEqualToValue={(o, v) => o.value === v?.value}
                            />
                        </Stack>
                        </Stack>
                        <Stack direction="column" spacing={1} flex={1} sx={{ overflow: 'auto', minHeight: 0 }}>
                            <FlightingPreview
                            triggerPlacement={triggerPlacement}
                            populatedFlights={populatedFlights}
                            setPopulatedFlights={setPopulatedFlights}
                            chipDisplayFields={[chipDisplayField1 || null, chipDisplayField2 || null]}
                        />
                        </Stack>
                    </Stack>
                </Popover>
            </Stack>



        </TabPanel>

    )
}
